import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom'
import './index.css'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import EditorSkeleton from './components/EditorSkeleton'
import { initMetaPixel } from './lib/meta-pixel'

initMetaPixel();

const App = lazy(() => import('./App.tsx'))
const Landing = lazy(() => import('./pages/Landing.tsx'))
const Templates = lazy(() => import('./pages/Templates.tsx'))
const Showcase = lazy(() => import('./pages/Showcase.tsx'))
const Faq = lazy(() => import('./pages/Faq.tsx'))
const Terms = lazy(() => import('./pages/Terms.tsx'))
const Privacy = lazy(() => import('./pages/Privacy.tsx'))
const ResetPassword = lazy(() => import('./pages/ResetPassword.tsx'))
const AuthModal = lazy(() => import('./components/AuthModal.tsx'))

function AuthModalPortal() {
  const { user, authModalOpen, closeAuthModal, sessionExpired, authModalDefaultView } = useAuth();
  const [searchParams] = useSearchParams();

  // Session expiry: non-dismissable modal with banner
  if (sessionExpired) {
    return (
      <AuthModal
        onClose={() => {}}
        banner="Your session has expired. Sign back in to continue."
      />
    );
  }

  // Suppress modal when user just signed in and is about to be redirected
  if (user && searchParams.get('returnTo')) return null;

  if (!authModalOpen) return null;
  return <AuthModal onClose={closeAuthModal} initialView={authModalDefaultView} />;
}

// ── Dev-only token probe ───────────────────────────────────────────────────────
// Runs once at startup. Logs any CSS custom property that resolves to empty
// string, which indicates the token CSS files failed to load or define it.
if (import.meta.env.DEV) {
  const PROBE_TOKENS = [
    '--color-bg-primary',
    '--color-bg-app',
    '--color-text-primary',
    '--color-border-primary',
    '--color-bg-brand-solid',
    '--neutral-500',
    '--font-sans',
  ] as const;
  const style = getComputedStyle(document.documentElement);
  const missing = PROBE_TOKENS.filter(t => !style.getPropertyValue(t).trim());
  if (missing.length > 0) {
    console.warn('[token-probe] The following CSS custom properties resolved empty — check that primitives.css / semantic.css are loading:', missing);
  } else {
    console.debug('[token-probe] All probed tokens resolved ✓');
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Suspense fallback={null}><Landing /></Suspense>} />
            <Route path="/templates" element={<Suspense fallback={null}><Templates /></Suspense>} />
            <Route path="/showcase" element={<Suspense fallback={null}><Showcase /></Suspense>} />
            <Route path="/faq" element={<Suspense fallback={null}><Faq /></Suspense>} />
            <Route path="/terms" element={<Suspense fallback={null}><Terms /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={null}><Privacy /></Suspense>} />
            <Route path="/editor" element={<Suspense fallback={<EditorSkeleton />}><App /></Suspense>} />
            <Route path="/reset" element={<Suspense fallback={null}><ResetPassword /></Suspense>} />
          </Routes>
          <AuthModalPortal />
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
)
